/**
 * TanStack Query keys and hooks.
 *
 * Keys are hierarchical so a mutation can invalidate narrowly: pricing a product invalidates
 * the pricing queue and the dashboard counters (the row leaves the queue and two counters
 * move), but not the orders board.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { meQueryKey } from '@most/auth';
import * as API from './api';
import type {
  AdminCategory,
  ChatAttachmentKind,
  AdminProduct,
  AdminRole,
  AdminRoleWrite,
  AdminUserListItem,
  CategoryInput,
  CreateUserRequest,
  NotificationChannel,
  NotificationStatus,
  OrderStatus,
  OrderStatusTarget,
  Role,
  UserStatus,
} from './types';

export const qk = {
  config: ['config'] as const,

  telegramStatus: ['telegram', 'status'] as const,

  dashboard: ['admin', 'dashboard'] as const,

  producers: (f: object) => ['admin', 'producers', f] as const,
  producer: (id: string) => ['admin', 'producer', id] as const,

  pricingQueue: (f: object) => ['admin', 'pricing-queue', f] as const,
  product: (id: string) => ['admin', 'product', id] as const,

  orders: (f: object) => ['admin', 'orders', f] as const,
  order: (number: string) => ['admin', 'order', number] as const,

  users: (f: object) => ['admin', 'users', f] as const,
  user: (id: string) => ['admin', 'user', id] as const,

  categories: ['admin', 'categories'] as const,

  notifications: (f: object) => ['admin', 'notifications', f] as const,
  notification: (id: string) => ['admin', 'notification', id] as const,

  audit: (f: object) => ['admin', 'audit', f] as const,

  monitoring: ['admin', 'monitoring'] as const,
  permissions: ['admin', 'settings', 'permissions'] as const,
  roles: ['admin', 'settings', 'roles'] as const,

  chats: ['admin', 'chats'] as const,
  chatMessages: (id: string) => ['admin', 'chat', id, 'messages'] as const,
} as const;

/**
 * Merge a mutation's OWN cache invalidation with the caller's `onSuccess`, so that both run and
 * neither can silently drop the other.
 *
 * This exists because the obvious spelling is wrong in a way that type-checks:
 *
 *     useMutation({ onSuccess: invalidate, ...opts })   // ← opts.onSuccess CLOBBERS invalidate
 *
 * Every screen passes an `onSuccess` (to fire a toast), so that ordering would have disabled
 * cache invalidation across the entire app while looking perfectly reasonable. Here the caller's
 * handler is called BY ours, after the invalidation, and the option order is not something a
 * future edit can get wrong.
 */
function useMergedOnSuccess<TData, TVars>(
  invalidate: (data: TData, vars: TVars) => void,
  callerOnSuccess: UseMutationOptions<TData, unknown, TVars>['onSuccess'],
) {
  // TanStack Query v5 hands `onSuccess` a fourth argument (the mutation context/meta). It is
  // forwarded untouched rather than dropped — a caller that reads it should get what the library
  // sent, not `undefined`.
  return useCallback(
    (data: TData, vars: TVars, ctx: unknown, extra: unknown) => {
      invalidate(data, vars);
      callerOnSuccess?.(data, vars, ctx as never, extra as never);
    },
    [invalidate, callerOnSuccess],
  );
}

// ── Profile (self-service) ───────────────────────────────────────────────────
export function useChangePassword(
  opts?: UseMutationOptions<void, unknown, { current_password: string; new_password: string }>,
) {
  return useMutation({
    ...opts,
    mutationFn: (body: { current_password: string; new_password: string }) =>
      API.changeMyPassword(body),
  });
}

/**
 * Linking finishes in ANOTHER app (Telegram), so nothing in this tab fires an invalidation
 * when it happens. `pollWhileUnlinked` covers the gap: while the profile screen is waiting
 * for a minted link to be tapped, the status refetches every few seconds and stops by itself
 * the moment `linked` flips.
 */
export function useTelegramStatus(pollWhileUnlinked = false) {
  return useQuery({
    queryKey: qk.telegramStatus,
    queryFn: API.getTelegramStatus,
    // A short staleTime also lets the default refetch-on-focus pick the new state up the
    // moment Merdan tabs back from Telegram.
    staleTime: 10_000,
    refetchInterval: pollWhileUnlinked
      ? (query) => (query.state.data?.linked ? false : 4_000)
      : false,
  });
}

export function useCreateTelegramLink() {
  return useMutation({ mutationFn: API.createTelegramLinkToken });
}

export function useUnlinkTelegram(opts?: UseMutationOptions<void, unknown, void>) {
  const qc = useQueryClient();
  const onSuccess = useMergedOnSuccess<void, void>(() => {
    void qc.invalidateQueries({ queryKey: qk.telegramStatus });
    // `Me.telegram_linked` just changed too.
    void qc.invalidateQueries({ queryKey: meQueryKey });
  }, opts?.onSuccess);

  return useMutation({ ...opts, mutationFn: () => API.unlinkTelegram(), onSuccess });
}

// ── Config ───────────────────────────────────────────────────────────────────
export function useAppConfig() {
  return useQuery({
    queryKey: qk.config,
    queryFn: API.getConfig,
    staleTime: Infinity, // Server constants. They do not move while a tab is open.
  });
}

// ── ADM-1 ────────────────────────────────────────────────────────────────────
export function useDashboard() {
  return useQuery({
    queryKey: qk.dashboard,
    queryFn: API.getAdminDashboard,
    // The websocket pushes queue.* / order.created / notification.dead and invalidates this
    // key, so a long staleTime is safe and the counters still move live (plans.md §6).
    staleTime: 60_000,
  });
}

// ── ADM-2 ────────────────────────────────────────────────────────────────────
export function useProducers(filters: { status?: UserStatus; page?: number; per_page?: number }) {
  return useQuery({
    queryKey: qk.producers(filters),
    queryFn: () => API.listAdminProducers(filters),
    placeholderData: keepPreviousData,
  });
}

export function useApproveProducer(opts?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  const onSuccess = useMergedOnSuccess<unknown, string>(() => {
    void qc.invalidateQueries({ queryKey: ['admin', 'producers'] });
    void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    void qc.invalidateQueries({ queryKey: qk.dashboard });
  }, opts?.onSuccess);

  return useMutation({
    ...opts,
    mutationFn: (id: string) => API.approveProducer(id),
    onSuccess,
  });
}

export function useRejectProducer(
  opts?: UseMutationOptions<unknown, unknown, { id: string; reason: string }>,
) {
  const qc = useQueryClient();
  const onSuccess = useMergedOnSuccess<unknown, { id: string; reason: string }>(() => {
    void qc.invalidateQueries({ queryKey: ['admin', 'producers'] });
    void qc.invalidateQueries({ queryKey: qk.dashboard });
  }, opts?.onSuccess);

  return useMutation({
    ...opts,
    mutationFn: ({ id, reason }: { id: string; reason: string }) => API.rejectProducer(id, reason),
    onSuccess,
  });
}

// ── ADM-3 ────────────────────────────────────────────────────────────────────
export function usePricingQueue(filters: { page?: number; per_page?: number }) {
  return useQuery({
    queryKey: qk.pricingQueue(filters),
    queryFn: () => API.getPricingQueue(filters),
    placeholderData: keepPreviousData,
  });
}

export function useAdminProduct(id: string | null) {
  return useQuery({
    queryKey: qk.product(id ?? ''),
    queryFn: () => API.getAdminProduct(id as string),
    enabled: Boolean(id),
  });
}

/**
 * Set OR clear the selling price. `null` is the withdraw path — it is how the undo-toast on
 * ADM-3 works, and it is the same column write the margin guard performs.
 */
export function useSetSellingPrice(
  opts?: UseMutationOptions<AdminProduct, unknown, { id: string; price: string | null }>,
) {
  const qc = useQueryClient();
  const onSuccess = useMergedOnSuccess<AdminProduct, { id: string; price: string | null }>(
    (data, vars) => {
      qc.setQueryData(qk.product(vars.id), data);
      // The row leaves (or re-enters) the queue, and both the unpriced and urgent counters move.
      void qc.invalidateQueries({ queryKey: ['admin', 'pricing-queue'] });
      void qc.invalidateQueries({ queryKey: qk.dashboard });
    },
    opts?.onSuccess,
  );

  return useMutation({
    ...opts,
    mutationFn: ({ id, price }: { id: string; price: string | null }) =>
      API.setSellingPrice(id, price),
    onSuccess,
  });
}

// ── ADM-4 / ADM-5 ────────────────────────────────────────────────────────────
export function useOrders(filters: {
  status?: OrderStatus;
  buyer_id?: string;
  producer_id?: string;
  page?: number;
  per_page?: number;
}) {
  return useQuery({
    queryKey: qk.orders(filters),
    queryFn: () => API.listAdminOrders(filters),
    placeholderData: keepPreviousData,
  });
}

export function useOrder(number: string) {
  return useQuery({
    queryKey: qk.order(number),
    queryFn: () => API.getAdminOrder(number),
    enabled: Boolean(number),
  });
}

export function useSetOrderStatus(
  opts?: UseMutationOptions<unknown, unknown, { number: string; status: OrderStatusTarget }>,
) {
  const qc = useQueryClient();
  const onSuccess = useMergedOnSuccess<unknown, { number: string; status: OrderStatusTarget }>(
    (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'orders'] });
      void qc.invalidateQueries({ queryKey: qk.order(vars.number) });
      void qc.invalidateQueries({ queryKey: qk.dashboard });
    },
    opts?.onSuccess,
  );

  return useMutation({
    ...opts,
    mutationFn: ({ number, status }: { number: string; status: OrderStatusTarget }) =>
      API.setOrderStatus(number, status),
    onSuccess,
  });
}

// ── ADM-6 ────────────────────────────────────────────────────────────────────
export function useUsers(filters: {
  role?: Role;
  status?: UserStatus;
  q?: string;
  page?: number;
  per_page?: number;
}) {
  return useQuery({
    queryKey: qk.users(filters),
    queryFn: () => API.listAdminUsers(filters),
    placeholderData: keepPreviousData,
  });
}

export function useUser(id: string | null) {
  return useQuery({
    queryKey: qk.user(id ?? ''),
    queryFn: () => API.getAdminUser(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateUser(
  opts?: UseMutationOptions<
    { user: AdminUserListItem; temporary_password: string },
    unknown,
    CreateUserRequest
  >,
) {
  const qc = useQueryClient();
  const onSuccess = useMergedOnSuccess<
    { user: AdminUserListItem; temporary_password: string },
    CreateUserRequest
  >(() => {
    void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
  }, opts?.onSuccess);

  return useMutation({
    ...opts,
    mutationFn: (body: CreateUserRequest) => API.createUser(body),
    onSuccess,
  });
}

/** Blocking kills every session the user holds, immediately. */
export function useBlockUser(
  opts?: UseMutationOptions<unknown, unknown, { id: string; block: boolean }>,
) {
  const qc = useQueryClient();
  const onSuccess = useMergedOnSuccess<unknown, { id: string; block: boolean }>(
    (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      void qc.invalidateQueries({ queryKey: qk.user(vars.id) });
    },
    opts?.onSuccess,
  );

  return useMutation({
    ...opts,
    mutationFn: ({ id, block }: { id: string; block: boolean }) =>
      block ? API.blockUser(id) : API.unblockUser(id),
    onSuccess,
  });
}

export function useVerifyEmailManually(opts?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  const onSuccess = useMergedOnSuccess<unknown, string>((_data, id) => {
    void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    void qc.invalidateQueries({ queryKey: qk.user(id) });
    void qc.invalidateQueries({ queryKey: qk.dashboard });
  }, opts?.onSuccess);

  return useMutation({
    ...opts,
    mutationFn: (id: string) => API.verifyUserEmailManually(id),
    onSuccess,
  });
}

/**
 * Two distinct endpoints, deliberately: a producer's rating is PUBLIC to buyers, a buyer's is
 * Merdan's private note. They are never the same call, and the UI must say which is which.
 */
export function useSetRating(
  opts?: UseMutationOptions<
    unknown,
    unknown,
    { id: string; rating: string | null; subject: 'producer' | 'buyer' }
  >,
) {
  type Vars = { id: string; rating: string | null; subject: 'producer' | 'buyer' };

  const qc = useQueryClient();
  const onSuccess = useMergedOnSuccess<unknown, Vars>((_data, vars) => {
    void qc.invalidateQueries({ queryKey: qk.user(vars.id) });
    void qc.invalidateQueries({ queryKey: qk.producer(vars.id) });
    void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
  }, opts?.onSuccess);

  return useMutation({
    ...opts,
    mutationFn: ({ id, rating, subject }: Vars) =>
      subject === 'producer' ? API.rateProducer(id, rating) : API.rateBuyer(id, rating),
    onSuccess,
  });
}

// ── ADM-7 ────────────────────────────────────────────────────────────────────
export function useCategories() {
  return useQuery({ queryKey: qk.categories, queryFn: API.listAdminCategories });
}

export function useSaveCategory(
  opts?: UseMutationOptions<AdminCategory, unknown, { id: string | null; body: CategoryInput }>,
) {
  type Vars = { id: string | null; body: CategoryInput };

  const qc = useQueryClient();
  const onSuccess = useMergedOnSuccess<AdminCategory, Vars>(() => {
    void qc.invalidateQueries({ queryKey: qk.categories });
  }, opts?.onSuccess);

  return useMutation({
    ...opts,
    mutationFn: ({ id, body }: Vars) =>
      id ? API.updateCategory(id, body) : API.createCategory(body),
    onSuccess,
  });
}

// ── ADM-8 ────────────────────────────────────────────────────────────────────
export function useNotifications(filters: {
  status?: NotificationStatus;
  channel?: NotificationChannel;
  order_id?: string;
  page?: number;
  per_page?: number;
}) {
  return useQuery({
    queryKey: qk.notifications(filters),
    queryFn: () => API.listAdminNotifications(filters),
    placeholderData: keepPreviousData,
  });
}

export function useNotificationDetail(id: string | null) {
  return useQuery({
    queryKey: qk.notification(id ?? ''),
    queryFn: () => API.getAdminNotification(id as string),
    enabled: Boolean(id),
  });
}

export function useRetryNotification(opts?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  const onSuccess = useMergedOnSuccess<unknown, string>((_data, id) => {
    void qc.invalidateQueries({ queryKey: ['admin', 'notifications'] });
    void qc.invalidateQueries({ queryKey: qk.notification(id) });
    // The failed/dead counters move — and the order detail's delivery table with them.
    void qc.invalidateQueries({ queryKey: qk.dashboard });
    void qc.invalidateQueries({ queryKey: ['admin', 'order'] });
  }, opts?.onSuccess);

  return useMutation({
    ...opts,
    mutationFn: (id: string) => API.retryNotification(id),
    onSuccess,
  });
}

// ── ADM-9 ────────────────────────────────────────────────────────────────────
export function useAuditLog(filters: {
  actor_id?: string;
  entity?: string;
  action?: string;
  from?: string;
  to?: string;
  page?: number;
  per_page?: number;
}) {
  return useQuery({
    queryKey: qk.audit(filters),
    queryFn: () => API.listAuditLog(filters),
    placeholderData: keepPreviousData,
  });
}

// ── Settings: Monitoring ─────────────────────────────────────────────────────
export function useMonitoring() {
  return useQuery({
    queryKey: qk.monitoring,
    queryFn: API.getMonitoring,
    // A live health readout: refetch on an interval so "pending is climbing" is visible without
    // a manual reload. Cheap — one composed call — and the screen is only open when Merdan is
    // actually watching.
    refetchInterval: 15_000,
    staleTime: 5_000,
  });
}

// ── Settings: Roles & Permissions ────────────────────────────────────────────
export function usePermissions() {
  return useQuery({
    queryKey: qk.permissions,
    queryFn: API.listPermissions,
    // The catalog is fixed and seeded; it never changes at runtime, so it need not refetch.
    staleTime: Infinity,
  });
}

export function useRoles() {
  return useQuery({ queryKey: qk.roles, queryFn: API.listRoles });
}

export function useSaveRole(
  opts?: UseMutationOptions<AdminRole, unknown, { id: string | null; body: AdminRoleWrite }>,
) {
  type Vars = { id: string | null; body: AdminRoleWrite };

  const qc = useQueryClient();
  const onSuccess = useMergedOnSuccess<AdminRole, Vars>(() => {
    void qc.invalidateQueries({ queryKey: qk.roles });
  }, opts?.onSuccess);

  return useMutation({
    ...opts,
    mutationFn: ({ id, body }: Vars) => (id ? API.updateRole(id, body) : API.createRole(body)),
    onSuccess,
  });
}

export function useDeleteRole(opts?: UseMutationOptions<void, unknown, string>) {
  const qc = useQueryClient();
  const onSuccess = useMergedOnSuccess<void, string>(() => {
    void qc.invalidateQueries({ queryKey: qk.roles });
    // A deleted role detaches its holders (users revert to unrestricted), so the user views change.
    void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
  }, opts?.onSuccess);

  return useMutation({ ...opts, mutationFn: (id: string) => API.deleteRole(id), onSuccess });
}

// ── Support chat inbox ───────────────────────────────────────────────────────

export function useChats() {
  return useQuery({ queryKey: qk.chats, queryFn: API.adminListChats, staleTime: 15_000 });
}

export function useChatMessages(id: string | null) {
  return useInfiniteQuery({
    queryKey: qk.chatMessages(id ?? 'none'),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => API.adminListChatMessages(id as string, pageParam, signal),
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled: !!id,
    staleTime: 10_000,
  });
}

export function useSendChatMessage(id: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => API.adminSendChatMessage(id as string, body),
    onSuccess: () => {
      if (id) void qc.invalidateQueries({ queryKey: qk.chatMessages(id) });
      void qc.invalidateQueries({ queryKey: qk.chats });
    },
  });
}

export function useUploadChatAttachment(id: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, file, caption }: { kind: ChatAttachmentKind; file: File | Blob; caption?: string }) =>
      API.adminUploadChatAttachment(id as string, kind, file, caption),
    onSuccess: () => {
      if (id) void qc.invalidateQueries({ queryKey: qk.chatMessages(id) });
      void qc.invalidateQueries({ queryKey: qk.chats });
    },
  });
}

export function useMarkChatRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => API.adminMarkChatRead(id),
    onSuccess: (_res, id) => {
      void qc.invalidateQueries({ queryKey: qk.chatMessages(id) });
      void qc.invalidateQueries({ queryKey: qk.chats });
    },
  });
}
